import {IServiceFactoryExtended} from "../../service-factory-extended.interface";
import {IJackpotBetsInfoModel} from "./jackpot-bets-info.model.interfeace";
import {IJackpotBetPrizeViewModel, JackpotBetPrizeType} from "./jackpot-bets-info.view-model.interface";
import {makeObservable, observable, runInAction} from "mobx";
import {IAmountAnimator} from "../../../../utils/amount-animator/amount-animator.interface";
import {AmountAnimator} from "../../../../utils/amount-animator/amount-animator";
import {TimeSpan} from "@sparkware/app-utils";
import JackpotBetsFeedRawDataInterface from "./jackpot-bets-feed-raw-data.interface";

export class JackpotBetsInfoModel implements IJackpotBetsInfoModel {
    constructor(rawJackpotBetsData: JackpotBetsFeedRawDataInterface,
                protected readonly services: IServiceFactoryExtended) {
        this._rawJackpotBetsInfo = rawJackpotBetsData;
        this._totalWinsAnimator = new AmountAnimator(() => this.totalWinsToday);
        this._prizes = rawJackpotBetsData.jackpotsPrizes.toDictionary(prize => prize.jackpotId,
                                                            prize => new JackpotBetPrizeModel(prize, this.services));
        this._startTotalWinsAnimation(0);
    }

    private _rawJackpotBetsInfo: JackpotBetsFeedRawDataInterface;

    @observable
    private readonly _prizes: Record<number, JackpotBetPrizeModel>;

    get winnersCount(): number {
        return this._rawJackpotBetsInfo.winnersCount;
    }

    get totalWinsToday(): number {
        return this._rawJackpotBetsInfo.totalWinsToday;
    }

    private readonly _totalWinsAnimator: IAmountAnimator;
    get animatedTotalWinsToday(): number {
        return this._totalWinsAnimator.currentValue;
    }

    getPrizes(): IJackpotBetPrizeViewModel[] {
        return this._prizes ? Object.values(this._prizes).sort((a,b) => {
            let amountA = a.amount;
            let amountB = b.amount;
            if (amountA > amountB) {
                return 1;
            } else if (amountB > amountA) {
                return -1;
            } else {
                return 0;
            }
        }) : [];
    }

    getJackpotBetPrizes(): IJackpotBetPrizeViewModel[] | null {
        return this._rawJackpotBetsInfo.jackpotsPrizes.map(prize => new JackpotBetPrizeModel(prize, this.services));
    }

    private _startTotalWinsAnimation(fromAmount: number): void {
        this._totalWinsAnimator.startAnimation({
            fromAmount: fromAmount,
            iterationDuration: TimeSpan.fromMilliseconds(250),
            animationDuration: TimeSpan.fromSeconds(3)
        })
    }

    updateRawInfo(newRawInfo: JackpotBetsFeedRawDataInterface): void {

        runInAction(() => {
            const previousTotalWins = this.totalWinsToday;
            this._rawJackpotBetsInfo = newRawInfo;
            this._startTotalWinsAnimation(previousTotalWins);

        })

        for(let prize of newRawInfo.jackpotsPrizes) {
            if(this._prizes[prize.jackpotId]) {
                this._prizes[prize.jackpotId].updateRawInfo(prize);
            } else {
                this._prizes[prize.jackpotId] = new JackpotBetPrizeModel(prize, this.services);
            }
        }
    }


}

type RawPrizeType = JackpotBetsFeedRawDataInterface["jackpotsPrizes"][number];

export class JackpotBetPrizeModel implements IJackpotBetPrizeViewModel {
    constructor(rawPrize: RawPrizeType, private readonly services: IServiceFactoryExtended) {
        this._rawPrize = rawPrize;
        makeObservable(this);

    }

    @observable.ref
    private _rawPrize: RawPrizeType;


    updateRawInfo(newRawInfo: RawPrizeType): void {
        runInAction(() => {
            this._rawPrize = newRawInfo;

        });
    }

    get jackpotId(): number {
        return this._rawPrize.jackpotId;
    }

    get type(): JackpotBetPrizeType {
        switch (this._rawPrize.jackpotId) {
            case 705:
                return JackpotBetPrizeType.Small;
            case 704:
                return JackpotBetPrizeType.Medium;
            case 703:
                return JackpotBetPrizeType.Big;
            case 702:
                return JackpotBetPrizeType.Large;
            case 701:
                return JackpotBetPrizeType.Mega;
            case 700:
                return JackpotBetPrizeType.Ultra;
            default:
                return JackpotBetPrizeType.Small;
        }
    }

    get amount(): number {
        return this._rawPrize.amount;
    }

    get label(): string {
        switch (this.type) {
            case JackpotBetPrizeType.Small:
                return this.services.translation.translate('Small');
            case JackpotBetPrizeType.Medium:
                return this.services.translation.translate('Medium');
            case JackpotBetPrizeType.Big:
                return this.services.translation.translate('Big');
            case JackpotBetPrizeType.Large:
                return this.services.translation.translate('Large');
            case JackpotBetPrizeType.Mega:
                return this.services.translation.translate('Mega');
            case JackpotBetPrizeType.Ultra:
                return this.services.translation.translate('Ultra');
            default:
                return 'Unknown';
        }
    }


}